// synced from loomweb blocks/remote-bash-block-view.tsx (de-Tailwind'd).
/**
 * Remote Bash command block (RemoteBashExecution). Faithful to loomweb's
 * RemoteBashBlockView: a header (icon + "Bash Command" with an optional
 * `@ <env>` remote-host label, a "Dangerous" flag, a copy button), the command
 * line, and a collapsible result panel. Tailwind classes are replaced by
 * namespaced `replay-*` CSS; lucide icons by the local inline-SVG set; mirrors
 * the ported command-block-view's output rendering via ToolResultPanel.
 */
import type { JSX } from "react";
import { useMemo } from "react";
import type { RemoteBashBlock } from "@shared/loom-message-block";
import { TerminalIcon, AlertTriangleIcon } from "./icons";
import { CopyButton } from "./copy-button";
import { ToolResultPanel } from "./tool-result-panel";
import { CommandLine } from "./command-line";
import { isDangerousCommand } from "./dangerous-command";

export function RemoteBashBlockView({ block }: { block: RemoteBashBlock }): JSX.Element {
  const isDangerous = useMemo(() => isDangerousCommand(block.command), [block.command]);
  const iconColor = isDangerous ? "replay-text-danger" : "replay-text-bash";
  // Prefer the human-readable remote host name; fall back to the env id.
  const envLabel = block.remoteHostname ?? block.envId;

  return (
    <div className="replay-block replay-remote-bash-block">
      <div className="replay-block-head">
        <TerminalIcon className={`replay-icon ${iconColor}`} />
        <span className="replay-block-label">
          Bash Command
          {envLabel !== undefined && envLabel.length > 0 && (
            <>
              {" @ "}
              <span className="replay-remote-host">{envLabel}</span>
            </>
          )}
        </span>
        {isDangerous && (
          <span className="replay-block-flag replay-text-danger">
            <AlertTriangleIcon className="replay-icon-xs" />
            Dangerous
          </span>
        )}
        <CopyButton text={block.command} className="replay-ml-auto" ariaLabel="复制命令" />
      </div>

      <div className="replay-command-line-wrap">
        <CommandLine command={block.command} />
      </div>

      {block.result?.output !== undefined && block.result.output.length > 0 && (
        <ToolResultPanel
          title="Output"
          content={block.result.output}
          copyAriaLabel="复制输出"
          defaultCollapsed={block.collapsed ?? true}
          status={block.status}
        />
      )}
    </div>
  );
}
